import React from 'react';
import { Info, ExternalLink, Shield, FileText, Building2, Heart } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';

// Standards with risk-focused explanations
const STANDARDS_INFO = {
  s1000d: {
    name: 'S1000D',
    fullName: 'S1000D Technical Publications',
    icon: FileText,
    industry: 'Aerospace & Defense',
    riskExplanation: 'Required for military equipment technical manuals and IETMs. Non-compliance blocks contractor approvals.',
    usedBy: 'Used by Boeing, Lockheed Martin, and defense contractors worldwide',
    mandatory: false,
    cost: '$500K-$2M to implement',
    color: 'blue'
  },
  dita: {
    name: 'DITA',
    fullName: 'Darwin Information Typing Architecture',
    icon: FileText,
    industry: 'All Industries',
    riskExplanation: 'Industry standard for modular, reusable technical content. Reduces documentation maintenance costs by 40%.',
    usedBy: 'IBM, Microsoft, Oracle, and 1000+ tech companies',
    mandatory: false,
    cost: '$30K-$150K to implement',
    color: 'green'
  },
  'hl7-fhir': {
    name: 'HL7 FHIR',
    fullName: 'HL7 Fast Healthcare Interoperability Resources',
    icon: Heart,
    industry: 'Healthcare',
    riskExplanation: 'Federal mandate for EHR systems. Non-compliance means losing Medicare/Medicaid contracts.',
    usedBy: 'Epic, Cerner, all major US healthcare systems',
    mandatory: true,
    cost: '$100K-$500K to implement',
    color: 'red'
  },
  'nist-800-53': {
    name: 'NIST 800-53',
    fullName: 'NIST Security Controls',
    icon: Shield,
    industry: 'Government & Cybersecurity',
    riskExplanation: 'Required for federal contractors. Failure to document controls = contract rejection and audit penalties.',
    usedBy: 'All federal agencies and their contractors',
    mandatory: true,
    cost: '$200K-$1M to implement',
    color: 'purple'
  },
  fedramp: {
    name: 'FedRAMP',
    fullName: 'Federal Risk and Authorization Management Program',
    icon: Building2,
    industry: 'Government Cloud',
    riskExplanation: 'Mandatory for cloud services used by federal government. Without this, you cannot sell to any federal agency.',
    usedBy: 'AWS GovCloud, Azure Government, all federal SaaS vendors',
    mandatory: true,
    cost: '$250K-$1M+ to certify',
    color: 'red'
  },
  'asd-ste100': {
    name: 'ASD-STE100',
    fullName: 'Simplified Technical English',
    icon: FileText,
    industry: 'Aviation, Defense, Manufacturing',
    riskExplanation: 'Reduces translation costs by 30% and eliminates ambiguity that causes equipment damage or safety incidents.',
    usedBy: 'Airbus, Boeing, Rolls-Royce, defense contractors',
    mandatory: false,
    cost: '$50K-$250K to implement',
    color: 'blue'
  },
  'ata-ispec-2200': {
    name: 'ATA iSpec 2200',
    fullName: 'ATA iSpec 2200',
    icon: FileText,
    industry: 'Aviation',
    riskExplanation: 'Required for airline maintenance documentation. Non-compliance blocks aircraft certification.',
    usedBy: 'All commercial airlines and MRO facilities worldwide',
    mandatory: true,
    cost: '$1M+ per airline',
    color: 'red'
  }
};

export function StandardExplainerTooltip({ standardId, children }) {
  const info = STANDARDS_INFO[standardId];
  
  if (!info) {
    return children;
  }

  const Icon = info.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent className="max-w-sm p-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Icon className="h-4 w-4" />
              <span className="font-semibold text-sm">{info.fullName}</span>
            </div>
            <p className="text-xs text-gray-300">{info.industry}</p>
            <p className="text-xs">{info.riskExplanation}</p>
            {info.mandatory && (
              <Badge className="bg-red-600 text-white text-xs">Mandatory</Badge>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function StandardExplainerCard({ standardId }) {
  const info = STANDARDS_INFO[standardId];
  
  if (!info) {
    return null;
  }

  const Icon = info.icon;
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
    purple: 'bg-purple-50 border-purple-200'
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[info.color]}`}>
      <div className="flex items-start space-x-3">
        <div className={`p-2 rounded-lg bg-${info.color}-100`}>
          <Icon className={`h-5 w-5 text-${info.color}-600`} />
        </div>
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="font-semibold text-gray-900">{info.fullName}</h3>
            {info.mandatory && (
              <Badge className="bg-red-100 text-red-700 text-xs">Mandatory</Badge>
            )}
          </div>
          <p className="text-xs text-gray-600 mb-2">{info.industry}</p>
          
          <div className="space-y-2">
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">Why This Matters:</p>
              <p className="text-xs text-gray-600">{info.riskExplanation}</p>
            </div>
            
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">Used By:</p>
              <p className="text-xs text-gray-600">{info.usedBy}</p>
            </div>
            
            <div className="pt-2 border-t border-current/10">
              <p className="text-xs text-gray-500">Implementation Cost: {info.cost}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StandardsExplainer({ selectedStandards = [] }) {
  if (!selectedStandards || selectedStandards.length === 0) {
    return (
      <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-gray-400 mt-0.5" />
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Understanding Standards</h3>
            <p className="text-sm text-gray-600 mb-4">
              Standards are not just formatting rules—they are risk mitigation tools that protect you from:
            </p>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start">
                <span className="text-red-600 mr-2">•</span>
                <span>Regulatory fines and contract rejections</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-600 mr-2">•</span>
                <span>Failed audits and certification delays</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-600 mr-2">•</span>
                <span>Safety incidents from unclear documentation</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-600 mr-2">•</span>
                <span>Vendor lock-in and expensive rewrites</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-3">
        <Info className="h-4 w-4 text-blue-600" />
        <h3 className="font-semibold text-gray-900">Selected Standards</h3>
      </div>
      {selectedStandards.map(standardId => (
        <StandardExplainerCard key={standardId} standardId={standardId} />
      ))}
    </div>
  );
}
